/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { CaseChangeEvent } from 'app/cm/lib/events';
import { AssessmentAttachments } from 'app/cm/components/assessments/assessment-attachments';
import * as CM from 'app/cm/models/essette-api';

const ATTACHMENT_ROUTE: string = 'cm.case.assessment';
const TASK_ROUTE: string = 'cm.task';
const INTERVENTION_ROUTE: string = 'cm.intervention';
const MCG_ATTACHMENT_ROUTE: string = 'cci.assessment';
const ICCC_ATTACHMENT_ROUTE: string = 'iccc.assessment';

@autoinject()
export class CaseAssessmentsPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected caseId!: string;
    protected readonly: boolean = false;

    protected assessmentsConfig: IAssessmentsConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        moduleCode: 'CM',
        generateAttachmentRoute: this.generateCaseRoute.bind(this),
        addTargetProperty: this.addCaseProperty.bind(this)
    }

    private hasOpenAttachment: boolean = false;
    private hasOpenMcgAttachment: boolean = false;
    private hasOpenIcccAttachment: boolean = false;
    private hasOpenTask: boolean = false;
    private hasOpenIntervention: boolean = false;

    private subscriptions: Array<Subscription> = [];


    ////****************** Element References ***************************************////
    protected $attachments!: AssessmentAttachments;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: CasePageContext,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async canActivate(params: { caseId: string }): Promise<boolean> {
        if(!params.caseId) { throw new Error('Missing or invalid caseId parameter'); }

        return true;
    }

    protected async activate(params: { caseId: string }): Promise<void> {
        if(this.caseId !== params.caseId) { this.caseId = params.caseId; }

        this.checkPermissions();

        this.subscriptions.push(
            this.events.subscribe(CaseChangeEvent, (e: CaseChangeEvent) => this.handleCaseChangeEvent(e))
        );
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected async handleCaseChangeEvent(e: CaseChangeEvent) {
        if(e.case && e.case.id === this.caseId) {
            await this.accountContext.load();
            const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');
            this.readonly = this.context.caseIsReadOnly(canEditClosedCase);
            this.$attachments.reload();
        };
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasOpenAttachment = this.accountContext.hasRouteAccess(ATTACHMENT_ROUTE);
        this.hasOpenMcgAttachment = this.accountContext.hasRouteAccess(MCG_ATTACHMENT_ROUTE);
        this.hasOpenIcccAttachment = this.accountContext.hasRouteAccess(ICCC_ATTACHMENT_ROUTE);
        this.hasOpenTask = this.accountContext.hasRouteAccess(TASK_ROUTE);
        this.hasOpenIntervention = this.accountContext.hasRouteAccess(INTERVENTION_ROUTE);
        const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');
        this.readonly = this.context.caseIsReadOnly(canEditClosedCase);
    }

    public generateCaseRoute(targetId: string, attachmentId: string, task?: CM.Task, externalSystemCode?: string): string | null {
        if(task) {
            const typeCode = task.attachedTo?.type?.code;

            switch(typeCode) {
                case 'CM.Case':
                    if(this.hasOpenTask) return this.router.generate(TASK_ROUTE, { taskId: task.id });
                    break;
                case 'CM.CarePlanGoal':
                    if(this.hasOpenIntervention) return this.router.generate(INTERVENTION_ROUTE, { interventionId: task.id });
                    break;
                default:
                    throw new Error(`Unexpected typeCode: ${typeCode}`);
            }
        }
        else if(!task) {
            if(!externalSystemCode && this.hasOpenAttachment) return this.router.generate(ATTACHMENT_ROUTE, { caseId: targetId, attachmentId: attachmentId });
            else if(!!externalSystemCode) {
                if(externalSystemCode == 'CCI' && this.hasOpenMcgAttachment) return this.router.generate(MCG_ATTACHMENT_ROUTE, { caseId: targetId, attachmentId: attachmentId });
                if(externalSystemCode == 'ICCC' && this.hasOpenIcccAttachment) return this.router.generate(ICCC_ATTACHMENT_ROUTE, { attachmentId: attachmentId });
            }
        }

        return null;
    }

    public addCaseProperty(attachment: CM.CaseAssessmentAttachment): void {
        attachment.case = { id: this.caseId };
    }


    ////****************** Service Calls ********************************************////

}
